import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { agents } from '../../../../../../api/agents'
import { usePipelineForm } from './PipelineFormContext'
import { usePipelineWorkspace } from './PipelineWorkspaceContext'

const PipelineChatContext = createContext({})

export const usePipelineChat = () => useContext(PipelineChatContext)

function PipelineChatProvider ({ children }) {
  const { code } = usePipelineForm()
  const { pipelineJson, variables } = usePipelineWorkspace()

  const [threadId, setThreadId] = useState(null)
  const [messages, setMessages] = useState([])
  const [agentStatus, setAgentStatus] = useState('idle')

  const { mutateAsync: invokeAgentMutation } = agents.useInvokeAgent()
  const { data: threadData } = agents.useAgentThread(threadId, {
    enabled: !!threadId
  })

  useEffect(() => {
    if (threadData) {
      setMessages(threadData.messages || [])
      if (threadData.status === 'done') {
        setAgentStatus('done')
      } else if (threadData.status === 'pending' || threadData.status === 'running') {
        setAgentStatus('processing')
      } else {
        setAgentStatus('idle')
      }
    }
  }, [threadData])

  const buildAttachments = useCallback(() => {
    const attachments = []
    try {
      const parsed = JSON.parse(pipelineJson)
      if (Array.isArray(parsed) && parsed.length > 0) {
        attachments.push({
          name: 'pipeline',
          content: parsed,
          ...(code ? { metadata: { pipelineCode: code } } : {})
        })
      }
    } catch {
      // Skip invalid JSON
    }

    const testInputs = { filters: {}, dates: {} }
    if (variables?.filters) testInputs.filters = variables.filters
    if (variables?.dates) testInputs.dates = variables.dates

    if (Object.keys(testInputs.filters).length > 0 || Object.keys(testInputs.dates).length > 0) {
      attachments.push({
        name: 'testInputs',
        content: testInputs
      })
    }

    return attachments
  }, [pipelineJson, variables, code])

  const invokeAgent = useCallback(async (message, attachments = []) => {
    try {
      setAgentStatus('processing')
      const result = await invokeAgentMutation({
        message,
        threadId: threadId || undefined,
        attachments
      })
      if (result.threadId) {
        setThreadId(result.threadId)
      }
      return result
    } catch (error) {
      setAgentStatus('error')
      throw error
    }
  }, [threadId, invokeAgentMutation])

  const value = useMemo(() => ({
    threadId,
    threadData,
    messages,
    agentStatus,
    setAgentStatus,
    invokeAgent,
    buildAttachments
  }), [
    threadId, threadData,
    messages, agentStatus,
    invokeAgent, buildAttachments
  ])

  return (
    <PipelineChatContext.Provider value={value}>
      {children}
    </PipelineChatContext.Provider>
  )
}

PipelineChatProvider.propTypes = {
  children: PropTypes.node
}

export default PipelineChatProvider
